import { useState } from 'react'
import { ArrowDownTrayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { cn } from 'src/utils/style'
import { Button } from 'src/components/button'
import { Switch } from 'src/components/switch'
import { getGuestDocuments } from 'src/services/reservation-guests/api'
import type { ReservationGuest } from 'src/services/reservation-guests'
import type { Reservation } from 'src/services/reservations/models'
import { getPresignedDownloadUrl } from 'src/services/survey/api'

type ReceiptData = NonNullable<Reservation['receipt']>

interface ReceiptDisplayProps {
  receipt: ReceiptData
  // The booker whose passport photo page and entry stamp the download fetches
  booker?: Pick<ReservationGuest, 'id' | 'formInstance'>
  currency?: string
  className?: string
}

function formatAmount(value: number | undefined, currency: string): string {
  if (value === undefined || value === null) return '-'
  return `${currency} ${Number(value).toLocaleString('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`
}

type IvaChoice = 'with' | 'without'

// Pill marking the toggle side we recommend.
function SuggestedTag() {
  const { t } = useTranslation()
  return (
    <span
      data-testid="iva-suggested-tag"
      className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
    >
      {t('reservations.ivaSuggested')}
    </span>
  )
}

// Fetches the booker's document keys from the audited endpoint, then opens each file
// through a presigned download. Nothing is cached or stored.
function DownloadDocumentsButton({
  booker,
}: {
  booker: Pick<ReservationGuest, 'id' | 'formInstance'>
}) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  const download = async () => {
    if (!booker.formInstance) return
    setLoading(true)
    setFailed(false)
    try {
      const { documents } = await getGuestDocuments({ reservationGuestId: booker.id })
      for (const doc of documents) {
        const { downloadUrl } = await getPresignedDownloadUrl({
          objectKey: doc.objectKey,
          instance: booker.formInstance,
        })
        window.open(downloadUrl, '_blank', 'noopener,noreferrer')
      }
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        icon={<ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />}
        onClick={download}
        disabled={loading}
        data-testid="receipt-download-documents"
      >
        {t('reservations.downloadDocuments')}
      </Button>
      {failed && (
        <span className="text-xs text-text-secondary" data-testid="receipt-documents-unavailable">
          {t('reservations.documentsUnavailable')}
        </span>
      )}
    </div>
  )
}

export function ReceiptDisplay({
  receipt,
  booker,
  currency = 'COP',
  className,
}: ReceiptDisplayProps) {
  const { t } = useTranslation()
  // Only recommend once the booker's residency is known: `paysIva` is false by
  // default, which would otherwise read as "exempt" for an unfilled survey.
  const suggestion: IvaChoice | null = receipt.ivaDetermined
    ? receipt.paysIva
      ? 'with'
      : 'without'
    : null
  const [showIva, setShowIva] = useState(suggestion !== 'without')
  // Invoicing without IVA needs the booker's entry stamp on file.
  const stampMissing = suggestion === 'without' && receipt.hasEntryStamp === false
  const hasData = receipt.total !== undefined

  if (!hasData) return null

  return (
    <div
      className={cn('rounded-xl border border-border bg-surface p-4 shadow-sm sm:p-6', className)}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold">{t('reservations.receipt')}</h2>
        <div className="flex items-center gap-2 text-sm">
          <span className="flex flex-col items-end gap-1">
            <span
              className={cn('text-text-secondary', !showIva && 'font-semibold text-text-primary')}
            >
              {t('reservations.withoutIva')}
            </span>
            {suggestion === 'without' && <SuggestedTag />}
          </span>
          <Switch
            checked={showIva}
            onCheckedChange={setShowIva}
            aria-label={t('reservations.withIva')}
            data-testid="iva-toggle"
          />
          <span className="flex flex-col items-start gap-1">
            <span
              className={cn('text-text-secondary', showIva && 'font-semibold text-text-primary')}
            >
              {t('reservations.withIva')}
            </span>
            {suggestion === 'with' && <SuggestedTag />}
          </span>
        </div>
      </div>

      <div
        data-testid="iva-suggestion"
        className={cn(
          'mb-4 rounded-lg border p-3 text-sm',
          stampMissing
            ? 'border-warning bg-yellow-50 text-text-primary'
            : 'border-accent-100 bg-accent-50 text-accent-800',
        )}
      >
        {suggestion === 'without' && (
          <>
            <p className="font-semibold">{t('reservations.ivaSuggestWithoutTitle')}</p>
            <p>{t('reservations.ivaSuggestWithoutBody')}</p>
            {stampMissing ? (
              <p
                className="mt-2 flex items-start gap-2 font-medium"
                data-testid="iva-stamp-missing"
              >
                <ExclamationTriangleIcon
                  className="mt-0.5 h-4 w-4 shrink-0 text-warning"
                  aria-hidden="true"
                />
                {t('reservations.ivaStampMissing')}
              </p>
            ) : (
              <p className="mt-1 font-medium" data-testid="iva-passport-reminder">
                {t('reservations.ivaPassportReminder')}
              </p>
            )}
          </>
        )}
        {suggestion === 'with' && (
          <>
            <p className="font-semibold">{t('reservations.ivaSuggestWithTitle')}</p>
            <p>{t('reservations.ivaSuggestWithBody')}</p>
          </>
        )}
        {suggestion === null && <p>{t('reservations.ivaSuggestPending')}</p>}
        {receipt.hasDocuments && booker?.formInstance && (
          <DownloadDocumentsButton booker={booker} />
        )}
      </div>

      <div className="space-y-3">
        {showIva && receipt.paysIva ? (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary">{t('reservations.preTax')}</span>
              <span className="font-medium text-text-primary">
                {formatAmount(receipt.preTax, currency)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary">IVA ({receipt.ivaRate})</span>
              <span className="font-medium text-text-primary">
                {formatAmount(receipt.ivaAmount, currency)}
              </span>
            </div>
            <div className="flex justify-between border-t border-border pt-2 text-sm">
              <span className="font-semibold text-text-primary">
                {t('reservations.receiptTotal')}
              </span>
              <span className="text-base font-bold text-primary">
                {formatAmount(receipt.total, currency)}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between text-sm">
              <span className="font-semibold text-text-primary">
                {t('reservations.receiptTotal')}
              </span>
              <span className="text-base font-bold text-primary">
                {formatAmount(receipt.total, currency)}
              </span>
            </div>
            {showIva && !receipt.paysIva && (
              <p className="text-xs text-text-placeholder" data-testid="iva-not-applicable">
                {t('reservations.ivaNotApplicable')}
              </p>
            )}
          </>
        )}
      </div>

      {showIva && (
        <div className="mt-4 space-y-1 border-t border-border pt-3">
          <p className="text-xs text-text-placeholder">
            {receipt.tipoDeDocumento} &middot; {receipt.numeracion}
          </p>
          <p className="text-xs text-text-placeholder">
            {receipt.impuesto} &middot; {receipt.tarifa}
          </p>
        </div>
      )}
    </div>
  )
}
